import { IFIReportType } from '@cims-online/shared'
import CIMSBasicFields from 'components/CIMSBasicFields'
import { HelpMessage } from 'utils/constants'
import { useProjectSettingsContext } from '../../../../../../ProjectSettingsContext'
import CIMSCollapse from 'components/CIMSCollapse'
import { Space, Typography } from 'antd'
import EnabledIndicator from '../../../../components/EnabledIndicator'
import { dateFormatter } from 'utils/dateFormatter'

const Help = HelpMessage.Administration.ProjectSettings.IFITracker
const {
  type: reportType,
  defaultMessages: {
    ambient,
    highTemp,
    lowTemp,
    highSlump,
    lowSlump,
    highAir,
    lowAir,
  },
} = IFIReportType.ConcreteTest

const ConcreteTestViewPanel: React.VFC = () => {
  const {
    concreteTestIFISettings: { data },
  } = useProjectSettingsContext()

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <CIMSBasicFields
        fields={[
          {
            label: 'Effective Date',
            help: Help.EffectiveDate,
            children: dateFormatter.simple(data?.effectiveDate),
          },
        ]}
      />
      <CIMSCollapse
        multiple
        panels={[
          {
            header: {
              children: 'Ambient Air',
            },
            key: `view-ambient-air-${IFIReportType.ConcreteTest.id}`,
            extra: {
              children: (
                <EnabledIndicator enabled={data?.ambientAir?.enabled} />
              ),
            },
            children: (
              <Space direction="vertical" size="middle">
                <Typography>
                  If the Air Temp is less than the Minimum Ambient Air
                  Temperature, issue an Identified Field Issue using the
                  Message.
                </Typography>
                <CIMSBasicFields
                  fields={[
                    {
                      label: 'Minimum Ambient Air Temperature',
                      help: Help.MinAmbientTemp,
                      children: data?.ambientAir?.minimumAmbientAirTemperature,
                    },
                    {
                      label: 'Message',
                      help: Help.FailureMessage(),
                      children: data?.ambientAir?.message ?? ambient,
                    },
                  ]}
                />
              </Space>
            ),
          },
          {
            header: {
              children: 'Temperature',
            },
            key: `view-temperature-${IFIReportType.ConcreteTest.id}`,
            extra: {
              children: (
                <EnabledIndicator enabled={data?.temperature?.enabled} />
              ),
            },
            children: (
              <Space direction="vertical" size="middle">
                <Typography>
                  Compare the Concrete Temp to the Concrete Temperature Range to
                  determine whether to issue an Identified Field Issue. If less
                  than, use the Low Temperature Message. If greater than, use
                  the High Temperature Message. If there is no Concrete Temp,
                  issue an Identified Field Issue with the message "No Concrete
                  Temp".
                </Typography>
                <CIMSBasicFields
                  fields={[
                    {
                      label: 'Concrete Temperature Range',
                      help: Help.TempRange,
                      children: data?.temperature?.tempRange,
                    },
                    {
                      label: 'High Temperature Message',
                      help: Help.FailureMessage({
                        reportType,
                        reason: 'above the High Temperature limit',
                      }),
                      children: data?.temperature?.highMessage ?? highTemp,
                    },
                    {
                      label: 'Low Temperature Message',
                      help: Help.FailureMessage({
                        reportType,
                        reason: 'below the Low Temperature limit',
                      }),
                      children: data?.temperature?.lowMessage ?? lowTemp,
                    },
                  ]}
                />
              </Space>
            ),
          },
          {
            header: {
              children: 'Slump',
            },
            key: `view-slump-${IFIReportType.ConcreteTest.id}`,
            extra: {
              children: <EnabledIndicator enabled={data?.slump?.enabled} />,
            },
            children: (
              <Space direction="vertical" size="middle">
                <Typography>
                  Compare the Slump to the Slump Range of the Concrete Mix
                  Design to determine whether to issue an Identified Field
                  Issue. If less than, use the Low Slump Message. If greater,
                  use the High Slump Message. If there is no Slump, issue an
                  Identified Field Issue with the message "No Slump".
                </Typography>
                <CIMSBasicFields
                  fields={[
                    {
                      label: 'High Slump Message',
                      help: Help.FailureMessage({
                        reportType,
                        reason: 'above the maximum allowed Slump',
                      }),
                      children: data?.slump?.highMessage ?? highSlump,
                    },
                    {
                      label: 'Low Slump Message',
                      help: Help.FailureMessage({
                        reportType,
                        reason: 'below the minimum allowed Slump',
                      }),
                      children: data?.slump?.lowMessage ?? lowSlump,
                    },
                  ]}
                />
              </Space>
            ),
          },
          {
            header: {
              children: 'Percent Air',
            },
            key: `view-percent-air-${IFIReportType.ConcreteTest.id}`,
            extra: {
              children: (
                <EnabledIndicator enabled={data?.percentAir?.enabled} />
              ),
            },
            children: (
              <Space direction="vertical" size="middle">
                <Typography>
                  Compare the Percent Air to the Air Range of the Concrete Mix
                  Design to determine whether to issue an Identified FIeld
                  Issue. If less than, use the Low Air Message. If greater than,
                  use the High Air Message. If there is no Percent Air, issue an
                  Identified Field Issue with the message "No Percent Air".
                </Typography>
                <CIMSBasicFields
                  fields={[
                    {
                      label: 'High Air Message',
                      help: Help.FailureMessage({
                        reportType,
                        reason: 'above the high Percent Air limit',
                      }),
                      children: data?.percentAir?.highMessage ?? highAir,
                    },
                    {
                      label: 'Low Air Message',
                      help: Help.FailureMessage({
                        reportType,
                        reason: 'below the low Percent Air limit',
                      }),
                      children: data?.percentAir?.lowMessage ?? lowAir,
                    },
                  ]}
                />
              </Space>
            ),
          },
        ]}
      />
    </Space>
  )
}

export default ConcreteTestViewPanel
